import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, fileToBase64, timeAgo } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { VerifiedBadge } from '../components/app/VerifiedBadge';
import { Send, ImagePlus, Mic, Square, ArrowLeft, Search, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

function VoiceBubble({ src }) {
  return <audio controls src={src} className="max-w-[220px] h-10" preload="metadata" />;
}

export default function MessagesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, t } = useApp();
  const [threads, setThreads] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef();
  const bottomRef = useRef();
  const lastTsRef = useRef('');

  // load threads
  const loadThreads = useCallback(() => {
    api.get('/dm/threads').then((r) => setThreads(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  // load other user + messages, with polling
  useEffect(() => {
    if (!userId) { setOtherUser(null); setMessages([]); return; }
    lastTsRef.current = '';
    api.get(`/users/${userId}`).then((r) => setOtherUser(r.data)).catch(() => {});
    const fetchMsgs = async (initial = false) => {
      try {
        const res = await api.get(`/dm/${userId}/messages${lastTsRef.current ? `?after=${encodeURIComponent(lastTsRef.current)}` : ''}`);
        if (res.data.length > 0) {
          lastTsRef.current = res.data[res.data.length - 1].created_at;
          setMessages((prev) => (initial ? res.data : [...prev, ...res.data]));
        } else if (initial) {
          setMessages([]);
        }
      } catch {}
    };
    setMessages([]);
    fetchMsgs(true);
    const iv = setInterval(() => fetchMsgs(false), 2000);
    return () => clearInterval(iv);
  }, [userId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // user search for new conversations
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const tm = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(searchQ)}`).then((r) => {
        setSearchResults(r.data.filter((u) => u.id !== user?.id));
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(tm);
  }, [searchQ, user?.id]);

  const send = async (type = 'text', content = null) => {
    const payload = { type, content: content ?? text };
    if (type === 'text' && !payload.content.trim()) return;
    try {
      const res = await api.post(`/dm/${userId}/messages`, payload);
      setMessages((prev) => [...prev, res.data]);
      lastTsRef.current = res.data.created_at;
      if (type === 'text') setText('');
      loadThreads();
    } catch { toast.error('Failed to send'); }
  };

  const sendImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Image too large (max 3MB)'); return; }
    const b64 = await fileToBase64(f);
    send('image', b64);
    e.target.value = '';
  };

  const toggleRecord = async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size > 2 * 1024 * 1024) { toast.error('Voice note too long'); return; }
        const b64 = await fileToBase64(blob);
        send('voice', b64);
        stream.getTracks().forEach((tr) => tr.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      toast.info('Recording... tap again to send');
    } catch {
      toast.error('Microphone access denied');
    }
  };

  // ============ thread list view ============
  if (!userId) {
    return (
      <div className="max-w-xl mx-auto px-3 sm:px-4 py-4">
        <h1 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk' }}>{t('messages')}</h1>
        <div className="relative mb-4">
          <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="dm-search-users-input" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
            placeholder={`${t('search')}...`} className="ps-9" />
        </div>
        {searchResults.length > 0 && (
          <div className="bg-card border border-border rounded-xl mb-4 overflow-hidden">
            {searchResults.map((u) => (
              <button key={u.id} data-testid="dm-search-result" onClick={() => { setSearchQ(''); navigate(`/messages/${u.id}`); }}
                className="w-full flex items-center gap-3 p-3 hover:bg-secondary/60 transition-colors">
                <Avatar className="w-10 h-10"><AvatarImage src={u.avatar} /><AvatarFallback>{u.username?.[0]}</AvatarFallback></Avatar>
                <div className="text-start">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium">{u.full_name}</p>
                    {u.verified && <VerifiedBadge size={13} />}
                  </div>
                  <p className="text-xs text-muted-foreground">@{u.username}</p>
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="space-y-1" data-testid="dm-thread-list">
          {threads.map((th) => (
            <button key={th.thread_id} data-testid="dm-thread-item" onClick={() => navigate(`/messages/${th.other_user.id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-card border border-transparent hover:border-border transition-colors">
              <Avatar className="w-11 h-11"><AvatarImage src={th.other_user?.avatar} /><AvatarFallback>{th.other_user?.username?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0 text-start">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-semibold truncate">{th.other_user?.full_name}</p>
                  {th.other_user?.verified && <VerifiedBadge size={13} />}
                  <span className="text-[10px] text-muted-foreground ms-auto">{timeAgo(th.last_message?.created_at)}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {th.last_message?.type === 'image' ? '[Photo]' : th.last_message?.type === 'voice' ? '[Voice note]' : th.last_message?.content}
                </p>
              </div>
            </button>
          ))}
          {threads.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <MessageCircle size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No conversations yet. Search for someone to start chatting!</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ chat thread view ============
  return (
    <div className="max-w-xl mx-auto flex flex-col h-[calc(100vh-8.5rem)] lg:h-screen">
      {/* header */}
      <div className="flex items-center gap-3 p-3 border-b border-border bg-card/50 sticky top-14 lg:top-0 z-30">
        <button data-testid="dm-back-button" onClick={() => navigate('/messages')} className="p-1.5 rounded-lg hover:bg-secondary">
          <ArrowLeft size={18} className="rtl:rotate-180" />
        </button>
        <Avatar className="w-9 h-9 cursor-pointer" onClick={() => navigate(`/profile/${otherUser?.id}`)}>
          <AvatarImage src={otherUser?.avatar} /><AvatarFallback>{otherUser?.username?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-sm font-semibold">{otherUser?.full_name}</p>
            {otherUser?.verified && <VerifiedBadge size={13} />}
          </div>
          <p className="text-xs text-muted-foreground">@{otherUser?.username}</p>
        </div>
      </div>

      {/* messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dm-messages-list">
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 ${mine ? 'bg-primary/15 border border-primary/25' : 'bg-secondary border border-border'}`}>
                {m.type === 'text' && <p className="text-sm whitespace-pre-wrap">{m.content}</p>}
                {m.type === 'image' && <img src={m.content} alt="shared" className="rounded-lg max-w-[240px] max-h-[300px] object-cover" />}
                {m.type === 'voice' && <VoiceBubble src={m.content} />}
                <p className="text-[10px] text-muted-foreground mt-1 text-end">{timeAgo(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* composer */}
      <div className="p-3 border-t border-border bg-card/50 flex items-center gap-2 sticky bottom-16 lg:bottom-0">
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={sendImage} />
        <button data-testid="dm-attach-image-button" onClick={() => fileRef.current?.click()}
          className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-primary transition-colors">
          <ImagePlus size={19} />
        </button>
        <button data-testid="dm-record-voice-button" onClick={toggleRecord}
          className={`p-2 rounded-lg transition-colors ${recording ? 'bg-destructive/20 text-destructive animate-pulse' : 'hover:bg-secondary text-muted-foreground'}`}>
          {recording ? <Square size={19} /> : <Mic size={19} />}
        </button>
        <Input data-testid="dm-message-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder={t('typeMessage')} className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && send()} />
        <Button data-testid="dm-send-button" size="sm" onClick={() => send()} className="h-10 px-4">
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
}
